// Carousel colors.
//
// A palette is exactly 3 colors, the same model as the brand themes in the account:
//   bg     = slide background
//   text   = titles and body
//   accent = highlighted keyword, badges, numbers, pills and pattern shapes
// Everything else (cards, muted text, ink drawn on the accent) is derived from these 3,
// so any palette (built-in, brand theme or custom) works with every layout.

export const PALETTES = {
  light: { name: "Light", bg: "#FAFAFA", text: "#111111", accent: "#111111" },
  "ink-navy": { name: "Ink navy", bg: "#12172B", text: "#F1F5F9", accent: "#7DD3FC" },
  "sand-forest": { name: "Sand forest", bg: "#F6F1E7", text: "#1F2937", accent: "#166534" },
  "pure-mono": { name: "Pure mono", bg: "#FFFFFF", text: "#111827", accent: "#4F46E5" },
  dark: { name: "Dark", bg: "#0A0A0A", text: "#FAFAFA", accent: "#22E58B" },
  mint: { name: "Mint", bg: "#E6F9EE", text: "#111111", accent: "#0B7A42" },
};

export const DEFAULT_PALETTE_ID = "light";
export const CUSTOM_PALETTE_ID = "custom";

// Below this ratio between text and background the Colors panel shows a warning (WCAG AA).
export const MIN_TEXT_CONTRAST = 4.5;

// "#abc", "abc" or "#AABBCC" -> "#AABBCC". Anything else -> the fallback.
export function normalizeHex(hex, fallback = "#000000") {
  let h = String(hex || "").trim().replace("#", "");
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split("").map((c) => c + c).join("");
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h.toUpperCase()}` : fallback;
}

const toRgb = (hex) => {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export function withAlpha(hex, alpha) {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function luminance(hex) {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// Mixes color a toward color b by t (0 = a, 1 = b).
export function mixHex(a, b, t) {
  const pa = toRgb(a);
  const pb = toRgb(b);
  return (
    "#" +
    pa
      .map((v, i) => Math.round(v * (1 - t) + pb[i] * t).toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
}

// Near-black or white, whichever reads better on the given color.
export function pickInk(hex) {
  return contrastRatio(hex, "#0A0A0A") >= contrastRatio(hex, "#FFFFFF") ? "#0A0A0A" : "#FFFFFF";
}

// A brand theme from the API ({ theme_id, name, background, text, highlight }) as a palette.
export function brandThemeToPalette(theme) {
  return {
    name: theme?.name || "Brand",
    bg: normalizeHex(theme?.background, "#FFFFFF"),
    text: normalizeHex(theme?.text, "#111111"),
    accent: normalizeHex(theme?.highlight, "#111111"),
  };
}

// paletteId is a built-in key, "custom", or a brand theme_id.
export function resolvePalette(paletteId, themes = [], customPalette = null) {
  if (paletteId === CUSTOM_PALETTE_ID && customPalette) return customPalette;
  if (PALETTES[paletteId]) return PALETTES[paletteId];
  const theme = (themes || []).find((th) => String(th.theme_id) === String(paletteId));
  return theme ? brandThemeToPalette(theme) : PALETTES[DEFAULT_PALETTE_ID];
}

// The colors a slide is drawn with, by role:
//   bg ~60% of the slide, surface = cards, muted = labels and subtitles,
//   accent ~10%, onAccent = readable ink for anything drawn ON the accent.
// "inverted" is used by the Bold layout on the first and last slide: the accent floods
// the whole background.
export function buildTheme(palette, inverted = false) {
  const p = palette || PALETTES[DEFAULT_PALETTE_ID];
  if (inverted) {
    const ink = pickInk(p.accent);
    return {
      bg: p.accent,
      text: ink,
      accent: ink,
      primary: ink,
      surface: mixHex(p.accent, ink, 0.1),
      muted: mixHex(ink, p.accent, 0.3),
      onAccent: p.accent,
    };
  }
  return {
    bg: p.bg,
    text: p.text,
    accent: p.accent,
    primary: p.accent,
    surface: mixHex(p.bg, p.text, 0.07),
    muted: mixHex(p.text, p.bg, 0.38),
    onAccent: pickInk(p.accent),
  };
}